import { useEffect, useRef, useState } from "react";
import "./MfaModal.css";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL
  ? process.env.REACT_APP_BASE_URL.replace("v1", "v2")
  : "http://localhost:5002/api/v2";

const SUPPORTED_FACTORS = ["SOFTWARE_TOKEN_MFA", "EMAIL_OTP"];

// A non-JSON body (502/HTML gateway error, empty response) must fall into the
// in-modal error path rather than throwing past it. Returns null in that case so
// callers can tell "the server did not answer in JSON" apart from "the API
// answered and rejected us" — the two need different messages.
async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_err) {
    return null;
  }
}

// GMA-1650: the login MFA step now supports two factors — an authenticator app
// (SOFTWARE_TOKEN_MFA) and an emailed code (EMAIL_OTP). loginNew tells us which
// case we're in:
//   - mfaSelectionRequired: user has BOTH enabled with no preferred one, so we
//     show a chooser first. Picking a method calls /mfa/select, which (for
//     email) sends the code and always returns a fresh session to validate with.
//   - mfaType === "EMAIL_OTP": email-only user; the code was already sent at
//     login, so go straight to code entry showing the masked destination.
//   - otherwise: authenticator (back-compatible default).
const MfaVerifyModal = ({
  isOpen,
  onClose,
  session: initialSession,
  onSuccess,
  email,
  mfaType: initialMfaType,
  mfaSelectionRequired = false,
  mfaOptions = [],
  destination: initialDestination,
}) => {
  const [code, setCode] = useState("");
  const [enterCodeError, setEnterCodeError] = useState("");
  const [session, setSession] = useState(initialSession);
  // Resolved factor to validate against; null while the user still has to choose.
  const [mfaType, setMfaType] = useState(
    mfaSelectionRequired ? null : initialMfaType || "SOFTWARE_TOKEN_MFA",
  );
  const [destination, setDestination] = useState(initialDestination);
  const [busy, setBusy] = useState(false);
  // Synchronous re-entrancy guard: `busy` is React state and flips on the next
  // render, so `disabled={busy}` alone lets a fast double-click fire two
  // requests that reuse the same one-time Cognito session. A ref updates
  // immediately, so the second click is rejected before it starts.
  const inFlight = useRef(false);
  // Abort + generation: closing/reopening must cancel the active fetch and
  // ignore its settle path — otherwise a stale response can call onSuccess or
  // clear the new attempt's guard while the same Cognito session is reused.
  const abortRef = useRef(null);
  const attemptIdRef = useRef(0);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    attemptIdRef.current += 1;
    setCode("");
    setEnterCodeError("");
    setSession(initialSession);
    setMfaType(
      mfaSelectionRequired ? null : initialMfaType || "SOFTWARE_TOKEN_MFA",
    );
    setDestination(initialDestination);
    // Clear any stuck in-flight state from a previous open (e.g. the modal was
    // closed while a request was still running), or the UI stays disabled.
    setBusy(false);
    inFlight.current = false;
  }, [
    isOpen,
    initialSession,
    initialMfaType,
    mfaSelectionRequired,
    initialDestination,
  ]);

  // Answer the SELECT_MFA_TYPE challenge. For EMAIL_OTP this is what sends the
  // email; Cognito consumes the sign-in session and returns a fresh one, so we
  // must validate against the session this call returns.
  const selectFactor = async (factor) => {
    // GMA-1369: selecting a factor also consumes the one-time Cognito session,
    // so guard re-entrant clicks. The ref (not `busy` state) closes the
    // same-tick double-click window state updates leave open.
    if (inFlight.current) return;
    inFlight.current = true;
    const myAttempt = attemptIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setEnterCodeError("");
    setBusy(true);
    try {
      const response = await fetch(`${BASE_URL}/users/mfa/select`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, mfaType: factor, session }),
        signal: controller.signal,
      });
      if (myAttempt !== attemptIdRef.current) return;
      const data = await parseJsonSafe(response);
      if (myAttempt !== attemptIdRef.current) return;

      if (!response.ok || !data || !data.success) {
        setEnterCodeError(
          "Could not start verification. Please close this and sign in again.",
        );
        return;
      }

      setSession(data.session);
      setMfaType(data.mfaType || factor);
      setDestination(data.destination);
    } catch (error) {
      if (error?.name === "AbortError") return;
      if (myAttempt !== attemptIdRef.current) return;
      toast.error("Error selecting verification method: " + error.message);
    } finally {
      if (myAttempt === attemptIdRef.current) {
        setBusy(false);
        inFlight.current = false;
        if (abortRef.current === controller) abortRef.current = null;
      }
    }
  };

  const handleSubmit = async () => {
    // GMA-1369 (ported from main 58a2f90): ignore re-entrant clicks while a
    // validation is in flight so we never replay the same Cognito session/code
    // ("session can only be used once" made login appear to fail).
    if (busy) {
      return;
    }
    setEnterCodeError("");
    if (code.length !== 6) {
      setEnterCodeError("Enter a 6-digit code.");
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    const myAttempt = attemptIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    try {
      const response = await fetch(`${BASE_URL}/users/mfa/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // mfaType routes the code to the right factor — an emailed code sent as
        // SOFTWARE_TOKEN_MFA (the old default) would always be rejected.
        body: JSON.stringify({ session, code, email, mfaType }),
        signal: controller.signal,
      });
      if (myAttempt !== attemptIdRef.current) return;
      const data = await parseJsonSafe(response);
      if (myAttempt !== attemptIdRef.current) return;

      // No parseable body means the request never reached the API intact (502
      // gateway page, empty response). Telling the user their code is wrong
      // sends them back to re-read their authenticator for a problem no code
      // entry can fix — say it is the service, not the code.
      if (!data) {
        setEnterCodeError(
          "Verification could not be completed right now. Please try again in a moment.",
        );
        return;
      }

      if (!response.ok || !data.success) {
        setEnterCodeError("Incorrect code. Try again.");
        return;
      }

      onSuccess(data);
    } catch (error) {
      if (error?.name === "AbortError") return;
      if (myAttempt !== attemptIdRef.current) return;
      toast.error("Error validating MFA: " + error.message);
    } finally {
      if (myAttempt === attemptIdRef.current) {
        setBusy(false);
        inFlight.current = false;
        if (abortRef.current === controller) abortRef.current = null;
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const needsSelection = mfaSelectionRequired && !mfaType;
  const selectableOptions = mfaOptions.filter((o) =>
    SUPPORTED_FACTORS.includes(o),
  );

  return (
    <div className="mfaModalBackdrop">
      <div className="mfaModal">
        {needsSelection ? (
          <>
            <h3>Choose how to verify</h3>
            {selectableOptions.length > 0 ? (
              <>
                <p>
                  Use your authenticator app or have a code emailed to you —
                  either one works.
                </p>
                <div className="mfaSubmit">
                  {selectableOptions.includes("SOFTWARE_TOKEN_MFA") && (
                    <button
                      onClick={() => selectFactor("SOFTWARE_TOKEN_MFA")}
                      className="btn-primary loginButtonUnique"
                      disabled={busy}
                    >
                      Use authenticator app
                    </button>
                  )}
                  {selectableOptions.includes("EMAIL_OTP") && (
                    <button
                      onClick={() => selectFactor("EMAIL_OTP")}
                      className="btn-primary loginButtonUnique"
                      disabled={busy}
                    >
                      Email me a code
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p>
                No verification method is available for this account. Please
                close this and sign in again.
              </p>
            )}
            {enterCodeError && <p className="errorMessage">{enterCodeError}</p>}
            <div className="mfaSubmit">
              <button
                onClick={onClose}
                className="btn-primary loginButtonUnique"
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h3>Enter your 2-factor authentication code</h3>
            <p>
              {mfaType === "EMAIL_OTP"
                ? `Enter the 6-digit code we emailed${
                    destination ? ` to ${destination}` : ""
                  } to sign in securely.`
                : "Check the authenticator app on your mobile device and enter the code to sign in securely."}
            </p>
            <input
              type="text"
              maxLength="6"
              placeholder="Enter your code"
              aria-label="Verification code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              style={{
                fontSize: "1.5rem",
                textAlign: "center",
              }}
            />
            {enterCodeError && <p className="errorMessage">{enterCodeError}</p>}
            <div className="mfaSubmit">
              <button
                onClick={onClose}
                className="btn-primary loginButtonUnique"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary loginButtonUnique"
                disabled={busy}
              >
                Verify
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MfaVerifyModal;
